import type {
  HandleError,
  Headers,
  Method,
  Params
} from './types'

const TOKEN_INFO = 'TOKEN_INFO' // The key used to store the token

const stringifyValues = (params: Params) =>
  Object.keys(params).reduce<Record<string, string>>((accumulator, key) => ({
    ...accumulator,
    [key]: String(params[key])
  }), {})

const addParams = (
  urlString: string,
  params: Params
) => {
  const url = new URL(urlString)

  Object.keys(params)
    .forEach(key =>
      url.searchParams.append(key, params[key])
    )

  return url
}

const buildHeader = (
  method: string,
  isUrlEncoded: boolean
) => {
  const usesBody = method !== 'get' && method !== 'delete'

  return !isUrlEncoded && usesBody
    ? {
      'Content-Type': 'application/json' // otherwise it will default to text/plain
    }
    : undefined
}

export const getFetchArguments = (
  url: string,
  method: Method,
  config: RequestInit,
  params: Params | undefined | any[],
  isUrlEncoded: boolean,
  formData: FormData | undefined
) => {
  const usesQueryString = method === 'get' || method === 'delete'

  const getBody = (
    stringParams: Params | undefined,
    params: Params | undefined | any[],
    formData: FormData | undefined
  ) => {
    if (formData) return formData

    return isUrlEncoded
      ? new URLSearchParams(stringParams) // automatically changes the content type header
      : JSON.stringify(params)
  }

  const stringParams = params
    ? stringifyValues(params)
    : undefined

  const finalUrl = stringParams && usesQueryString
    ? addParams(url, stringParams)
    : url

  const finalConfig = usesQueryString
    ? config
    : {
      ...config,
      body: getBody(stringParams, params, formData)
    }

  return [finalUrl, finalConfig]
}

export const retryRequestData = async (
  method: Method,
  isUrlEncoded: boolean,
  baseUrl: string,
  params: Params | undefined | any[],
  handleResponse: (response: Response, retryCount: number) => Promise<void>,
  catchFetchError: (error: { name: string; toString: () => any }) => void,
  retryCount: number,
  response: Response,
  handleError: HandleError,
  controller?: AbortController,
  extraHeaders?: Headers,
  formData?: FormData
) => {
  // Retrieve the stored data
  const tokenData = localStorage.getItem(TOKEN_INFO)

  // Parse the JSON string to an object
  const parsedTokenData = tokenData
    ? JSON.parse(tokenData)
    : null

  // Get the access_token
  const accessToken = parsedTokenData?.access_token

  if (accessToken) {
    // Update headers with the new token
    const newExtraHeaders = {
      ...extraHeaders,
      Authorization: `Bearer ${accessToken}`
    }

    const newHeaders = {
      ...newExtraHeaders,
      ...buildHeader(method, isUrlEncoded)
    }

    const newBaseConfig = {
      // only uppercase methods names are guaranteed acceptance
      method: method.toUpperCase(),
      headers: newHeaders,
      signal: controller && controller.signal
    }

    // Retry the request with updated headers
    const [newUrl, newConfig] = getFetchArguments(
      baseUrl,
      method,
      newBaseConfig,
      params,
      isUrlEncoded,
      formData
    )

    return fetch(newUrl as RequestInfo, newConfig as RequestInit)
      .then((newResponse: Response) => handleResponse(newResponse, retryCount + 1))
      .catch(catchFetchError)
  }

  // If not retriable or retries exhausted, handle error
  return handleError(await response.json().catch(() => ({})))
}
